// THE STATUS COMPUTATION — the one place a colour is decided. PURE: `now` is an
// argument, nothing reads a clock, nothing does I/O.
//
// Brief: _architect/briefs/2026-09-15-coverage-page-overhaul.md §3, §5, D6, D8
//
// ⚠️⚠️ THE COLOUR IS COMPUTED FROM A DECLARED EXPECTATION AGAINST MEASURED
// REALITY. It is never hand-assigned per source. A source that cannot supply the
// measurement says so (`unobserved`) rather than resolving to green.

import type {
  AccountHealth,
  JobHealth,
  SourceExpectation,
} from '../../types/sourceHealth.js';

export type HealthStatus = 'red' | 'amber' | 'green';

export interface StatusVerdict {
  status: HealthStatus;
  /** One sentence naming what is wrong. Null only when green. */
  reason: string | null;
}

/**
 * ⚠️⚠️ TWO, AND IT IS A MEASUREMENT RATHER THAN THE ARCHITECT'S GUESS (D6).
 *
 * Measured 2026-09-16 over 30 days of `reporting_runs`, mode `refresh`: 357 gaps
 * between run STARTS, p50 119 min, p90/p95/p99 all 120 min, ONE excursion at 359
 * min. At 2x (240 min) exactly one gap in 357 trips it — and that gap is a genuine
 * two-cycle miss, so it is a true positive rather than noise. The guess survives
 * contact with the data.
 *
 * ⚠️ IT JUDGES JOB LIVENESS ONLY, and that restriction is what makes it usable.
 * Applied instead to gaps between FULLY-SUCCESSFUL runs — the rule as originally
 * proposed — the same 30 days give p90 360 min, p99 1,439 min, and 42 of 184 gaps
 * (23%) over 2x. That is the "paints everything amber on a normal Tuesday" failure
 * the brief warns about, and its cause is that one failed account out of ~306
 * disqualifies an entire run.
 */
export const INTERVAL_TOLERANCE_MULTIPLE = 2;

function minutesSince(iso: string | null, nowMs: number): number | null {
  if (iso === null) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return (nowMs - t) / 60_000;
}

/**
 * A JOB's colour: did it run, and recently enough.
 *
 * ⚠️ `unobserved` is AMBER, never green. Google Ads has no run record we read, so
 * "we cannot see whether this job succeeded" is a real limitation and the page
 * says so. Resolving it to green would be the hardcoded green D1 exists to forbid.
 */
export function jobStatus(
  job: JobHealth,
  expectation: SourceExpectation,
  nowMs: number,
): StatusVerdict {
  if (job.lastRunOutcome === 'failed') {
    return { status: 'red', reason: job.lastRunMessage ?? 'The last run failed.' };
  }
  if (job.lastRunOutcome === 'never_run') {
    return { status: 'red', reason: 'This job has never run.' };
  }
  if (job.lastRunOutcome === 'presumed_failed') {
    // ⚠️ "presumed", never "failed" — we did not observe it (D4).
    return {
      status: 'red',
      reason: 'The last run never recorded a finish and is presumed to have failed.',
    };
  }
  if (job.lastRunOutcome === 'unobserved') {
    return {
      status: 'amber',
      reason: 'This job reports no run record, so its outcome cannot be checked.',
    };
  }
  if (job.lastRunOutcome === 'partial') {
    return { status: 'amber', reason: job.lastRunMessage ?? 'The last run partly failed.' };
  }

  const age = minutesSince(job.lastRunAt, nowMs);
  // ⚠️ A run in flight is not late. It is running now.
  if (job.lastRunOutcome === 'running') return { status: 'green', reason: null };
  if (age === null) {
    return { status: 'amber', reason: 'The last run time could not be read.' };
  }
  const tolerance = expectation.expectedIntervalMinutes * INTERVAL_TOLERANCE_MULTIPLE;
  if (age > tolerance) {
    const hours = Math.round((age / 60) * 10) / 10;
    return { status: 'amber', reason: `No run in ${hours}h; one is expected every ${Math.round(expectation.expectedIntervalMinutes / 60)}h.` };
  }
  return { status: 'green', reason: null };
}

/**
 * An ACCOUNT's colour.
 *
 * ⚠️⚠️ STALENESS IS ONLY EVALUATED WHERE IT MEANS SOMETHING. A source whose facts
 * record ACTIVITY rather than delivery (Google Ads: no rows on a day with no
 * spend) cannot be judged on `dataThrough` — measured 2026-09-16, seven customers
 * inside a healthy MCC have no spend rows at all. Judging them would paint seven
 * dealerships red for not advertising, which is a worklist telling you to fix
 * something that is not broken.
 */
export function accountStatus(
  account: AccountHealth,
  expectation: SourceExpectation,
  nowMs: number,
  /**
   * ⚠️⚠️ IS THE SOURCE'S OWN PULL BEHIND? REQUIRED, so every call site has to
   * answer it (§5). An optional flag defaulting to false would let a caller
   * silently keep the per-account noise this parameter exists to remove, and the
   * page would go on rendering one outage as 234 account faults.
   */
  sourceStale: boolean,
): StatusVerdict {
  // Connected but nothing ever landed.
  //
  // ⚠️⚠️ THIS CALLED ITSELF "UNAMBIGUOUS" UNTIL 2026-09-16 AND IT IS NOT — the
  // same delivery/activity collapse as the staleness branch below, one rule
  // earlier, found by building Google Ads on top of it. "No data has ever landed"
  // has two causes and only one is a fault:
  //
  //     delivery confirmed   we ARE reaching this account; it has never spent.
  //                          A commercial fact, not a pipeline one. GREEN.
  //     delivery unknown     we cannot say we ever reached it. RED, as before.
  //
  // Measured 2026-09-16: TEN Google Ads customers are delivered and have no spend
  // rows at all. Calling them red is a false positive of exactly the class D12
  // exists to remove, and it would have arrived with step 4 rather than being
  // fixed by it.
  //
  // ⚠️ The delivery check is DELIBERATELY the same recency test the staleness
  // branch uses, so "recent" cannot come to mean two different things in one
  // function.
  if (account.dataThrough === null && account.coverageFrom === null) {
    const delivery = minutesSince(account.deliveryConfirmedThrough, nowMs);
    const tolerance = expectation.expectedIntervalMinutes * INTERVAL_TOLERANCE_MULTIPLE;
    if (delivery !== null && delivery <= tolerance) {
      return { status: 'green', reason: null };
    }
    return { status: 'red', reason: 'Connected, but no data has ever loaded.' };
  }

  // ⚠️⚠️ TWO DIFFERENT TOLERANCES LIVE IN THIS FUNCTION AND THAT IS THE POINT
  // (2026-09-22). They look like an inconsistency and are not:
  //
  //   DELIVERY recency   `expectedIntervalMinutes × MULTIPLE` — "did the pull
  //                      reach this account recently". A question about the JOB,
  //                      so the run cadence is the right yardstick.
  //   DATA freshness     `expectedDataFreshnessMinutes` — "how old is the newest
  //                      row". A question about the SOURCE's own reporting lag,
  //                      which has nothing to do with how often we ask.
  //
  // ⚠️ They were ONE number until a two-hour outage turned the whole Meta roster
  // amber reading "Data stops 0.4 days ago" — the run cadence being applied to
  // data that Meta itself publishes hours late.
  if (account.freshnessMeaningful && expectation.expectedDataFreshnessMinutes !== null) {
    const age = minutesSince(account.dataThrough, nowMs);
    if (age === null) {
      return { status: 'amber', reason: 'The latest data date could not be read.' };
    }
    const tolerance = expectation.expectedDataFreshnessMinutes;
    if (age > tolerance) {
      // ⚠️⚠️ DELIVERY DECIDES WHETHER OLD ACTIVITY IS A FAULT. `dataThrough` alone
      // cannot tell a DORMANT account (we reached it; it had nothing to report)
      // from a STALE one (we stopped reaching it), and calling the first a fault
      // is a red row for an account that is working exactly as intended.
      //
      // ⚠️ NULL IS NOT A NEGATIVE. A run predating the `succeeded_accounts` roster
      // reads null, which means "we cannot say", and an account is NOT called
      // dormant on missing evidence — it keeps the staleness verdict it would have
      // had before this rule existed. Silence is not confirmation of delivery.
      // ⚠️ THE DELIVERY CHECK KEEPS THE RUN CADENCE, for the reason above: it is
      // asking whether the PULL reached this account, not whether the DATA is
      // fresh. Using the data tolerance here would call an account "delivered"
      // two days after the pull last touched it.
      const deliveryTolerance = expectation.expectedIntervalMinutes * INTERVAL_TOLERANCE_MULTIPLE;
      const delivery = minutesSince(account.deliveryConfirmedThrough, nowMs);
      if (delivery !== null && delivery <= deliveryTolerance) {
        return { status: 'green', reason: null };
      }
      // ⚠️⚠️ THE SOURCE'S OWN OUTAGE IS NOT THIS ACCOUNT'S FAULT (§5). When the
      // pull itself has not completed recently, EVERY account's data is old for
      // one reason, and reporting it per account turns one pipeline failure into
      // a roster of red herrings — 234 of them, measured 2026-09-22. The finding
      // is reported ONCE at source level instead; see `staleSourceFindings`.
      //
      // ⚠️ SUPPRESSED, NOT DELETED. With the pull healthy this still fires, which
      // is the case it exists for: an account stale for its OWN reasons while
      // everything else is current. The fact stays reachable either way — the
      // detail line always shows `data to <date>`.
      if (sourceStale) return { status: 'green', reason: null };
      const days = Math.round((age / 1440) * 10) / 10;
      return { status: 'amber', reason: `Data stops ${days} days ago.` };
    }
  }

  if (expectation.expectedWindowDays !== null && account.coverageFrom !== null) {
    const held = minutesSince(account.coverageFrom, nowMs);
    if (held !== null && held / 1440 < expectation.expectedWindowDays) {
      const days = Math.floor(held / 1440);
      return {
        status: 'amber',
        reason: `Only ${days} days of history; ${expectation.expectedWindowDays} expected.`,
      };
    }
  }

  return { status: 'green', reason: null };
}

/** Worst-first ordering, so the page can render red before amber. */
export const STATUS_RANK: Record<HealthStatus, number> = { red: 0, amber: 1, green: 2 };
